import { HTTP_INTERCEPTORS, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable, Provider } from '@angular/core';
import { MunicipalityTokenService } from './municipality-token.service';
import { FsessionService } from '../session/fsession.service';
import { BehaviorSubject, catchError, filter, from, Observable, switchMap, take, throwError } from 'rxjs';
import { DatabaseService } from '../database/database.service';
import { configuracion } from 'src/configuracion';
import { updateTokensAndRefresh } from './token-utils';
import { TokenRefreshService } from './token-refresh.service';

@Injectable()
export class MunicipalityTokenInterceptor implements HttpInterceptor {
  private municipalityTokenService = inject(MunicipalityTokenService);
  private fSesionService = inject(FsessionService);
  private databaseService = inject(DatabaseService);
  private tokenRefreshService = inject(TokenRefreshService);

  isRefreshing = false;
  refreshSubject = new BehaviorSubject<string | null>(null);

  constructor() {
    this.municipalityTokenService.initialize();
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler) {
    const isTranslationsFiles = req.url.includes(configuracion.translations_URL);
    const isStreamingRequest = req.url.includes(configuracion.ws_Stream_new);
    const isOldStreamingRequest = req.url.includes(configuracion.servidor_Stream);
    const isRefreshRequest = req.url.includes('/auth/refresh');
    const isNewVersion = req.url.includes('/getLatestRelease');

    if (isTranslationsFiles || isRefreshRequest || isStreamingRequest || isOldStreamingRequest || isNewVersion) {
      return next.handle(req);
    }

    const storedToken = this.fSesionService.optenerValor('clientToken', 'municipality');
    const memoryToken = this.municipalityTokenService.getToken(true);
    const token = typeof storedToken === 'string' ? storedToken : typeof memoryToken === 'string' ? memoryToken : null;

    if (typeof storedToken !== 'string' && token) {
      this.fSesionService.agregarActualizar('clientToken', token, 'municipality');
    }

    const authedReq = token ? this.addToken(req, token) : req;

    return next.handle(authedReq).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.status === 401 && error.error?.code === 'TOKEN_EXPIRED' && !isRefreshRequest) {
          return this.handleRefresh(req, next);
        }
        return throwError(() => error);
      }),
    );
  }
  private addToken(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return req.clone({
      setHeaders: { 'Municipality-Authorization': `Bearer ${token}` },
    });
  }

  private handleRefresh(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.isRefreshing) {
      return this.refreshSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) => next.handle(this.addToken(req, token!))),
      );
    }

    this.isRefreshing = true;
    this.refreshSubject.next(null);

    return this.databaseService.municipalityRefreshToken().pipe(
      switchMap(({ accessToken, refreshToken }) => {
        return from(this.storeAndVerifyTokens(accessToken, refreshToken)).pipe(
          switchMap(() => {
            this.isRefreshing = false;
            this.refreshSubject.next(accessToken);
            return next.handle(this.addToken(req, accessToken));
          }),
        );
      }),
      catchError((err) => {
        this.isRefreshing = false;
        return throwError(() => err);
      }),
    );
  }

  private async storeAndVerifyTokens(accessToken: string, refreshToken: string): Promise<void> {
    await Promise.all([
      this.municipalityTokenService.verifyAndStoreToken(accessToken),
      this.municipalityTokenService.verifyToken(refreshToken),
    ]);
    updateTokensAndRefresh(
      this.fSesionService,
      this.municipalityTokenService,
      this.tokenRefreshService,
      accessToken,
      refreshToken,
      'schedule',
    );
  }
}

export const MUNICIPALITY_TOKEN_PROVIDER: Provider = {
  provide: HTTP_INTERCEPTORS,
  useClass: MunicipalityTokenInterceptor,
  multi: true,
};
